<script lang="ts" setup>
import type { MediaLoadingStrategy } from 'vidstack'
import type { VTTCue } from 'media-captions'
import type { AudioPlayerVariant } from '~/models/Media'
import type { ButtonSizes } from '~/components/ui/Button.vue'
import { useI18n } from 'vue-i18n'
import { computed, defineAsyncComponent, h, nextTick, ref, useAttrs, watch } from 'vue'
import { MediaPlayer } from 'vidstack'
import { PopoverAnchor, PopoverContent, PopoverRoot, PopoverTrigger } from 'reka-ui'
import 'vidstack/bundle'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { faCircle, faCircleDot, faInfoCircle, faText, faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import IconInputSwitch from '~/components/utils/IconInputSwitch.vue'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'
import AuPopover from '~/components/ui/AuPopover.vue'
import TimeSlider from '~/components/media/TimeSlider.vue'
import Captions from '~/components/media/Captions.vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<{
  mediaId?: string
  variant?: AudioPlayerVariant
  load?: MediaLoadingStrategy
  dataPendo?: string
  hideButtonWhenOpen?: boolean
  aiGenerated?: boolean
  size?: ButtonSizes
  src?: string
}>(), {
  variant: 'inline',
  load: 'play',
  aiGenerated: false,
  size: 'lg',
})

const emit = defineEmits([
  'update:transcription',
  'update:showTranscription',
  'play',
  'pause',
  'open',
  'close',
])

const AsyncWrapper = defineAsyncComponent(async () => h('slot'))

const attrs = useAttrs()
const attrsWithoutSrc = computed(() => {
  const { src, ...rest } = attrs
  return rest
})

const { t } = useI18n()
const isPlaying = ref(false)
const isActive = ref(false)
const playerEl = ref<MediaPlayer>()
const cues = ref<readonly VTTCue[]>([])
const showTranscription = ref(false)
const isInline = computed(() => props.variant === 'inline')
const internalSrc = ref<string>('')

watch(() => props.src, (newSrc) => {
  if (!newSrc) {
    internalSrc.value = ''
    return
  }
  if (playerEl.value) {
    internalSrc.value = newSrc
  } else {
    const stopWatcher = watch(playerEl, (player) => {
      if (player) {
        internalSrc.value = newSrc
        stopWatcher()
      }
    })
  }
}, { immediate: true })

async function close() {
  if (!playerEl.value?.paused) {
    await playerEl.value?.pause()
  }

  isActive.value = false
  showTranscription.value = false
}

function activate() {
  isActive.value = true

  // We have to wait for next tick to make sure the video player is moved into the teleported slot, else it might trigger a src change event and pause itself
  nextTick(() => {
    setTimeout(() => playerEl.value?.play(), 500)
  })
}

watch(isActive, async (value) => {
  if (value && (closePrevActive.value !== close)) {
    await closePrevActive.value?.()
    closePrevActive.value = close
  }

  emit(value ? 'open' : 'close')
})

watch(isPlaying, value => emit(value ? 'play' : 'pause'))

watch(playerEl, (value) => {
  if (value) {
    value.textTracks.addEventListener('add', ({ target }) => {
      const [track] = target.getByKind('metadata')
      if (track) {
        track.addEventListener('load', () => {
          cues.value = track.cues
        })
      }
    })
  }
})

watch(cues, value => emit('update:transcription', value), { deep: true })

watch(showTranscription, (value) => {
  emit('update:showTranscription', value)
})

defineExpose({ close })
</script>

<script lang="ts">
const closePrevActive = ref<null | (() => Promise<void>)>(null)
</script>

<template>
  <!-- eslint-disable vue/no-multiple-template-root -->
  <template v-if="!isInline && (hideButtonWhenOpen ? !isActive : true)">
    <slot
      :activate
      :close
      :is-playing
      :is-active
      :pause="() => playerEl?.pause()"
      :play="() => playerEl?.play()"
      name="trigger"
    >
      <Button
        v-if="variant === 'big-button-no-text'"
        class="group grid size-32 -translate-x-1/2 cursor-pointer place-content-center rounded-full bg-(--theme-5) text-3xl text-(--theme-50) shadow-lg hover:not-disabled:text-[rgb(from_var(--theme-20)_r_g_b_/0.8)]"
        shape="square"
        :data-pendo
        :size
        @click="isPlaying ? playerEl?.pause() : activate()"
      >
        <font-awesome-icon
          :class="{ 'translate-x-[5%]': !isPlaying }"
          :icon="isPlaying ? faPause : faPlay"
          size="xl"
        />
      </Button>

      <Button
        v-else
        :data-pendo
        class="bg-[var(--media-color,var(--theme-5,currentColor))] text-[var(--media-bg-color,var(--theme-50,currentColor))] outline-[var(--media-bg-color,var(--theme-50,currentColor))] hover:text-[var(--media-color,var(--theme-60,currentColor))] hover:not-disabled:bg-[var(--media-bg-color,var(--theme-10,currentColor))]"
        :size
        variant="outline"
        @click="isPlaying ? playerEl?.pause() : activate()"
      >
        <template v-if="isPlaying">
          <font-awesome-icon
            :icon="faPause"
          />
          <slot :is-playing name="button-text">
            {{ $t('media.pauseAudio') }}
          </slot>
        </template>

        <template v-else>
          <font-awesome-icon
            :icon="faPlay"
            class="scale-95"
          />
          <slot :is-playing name="button-text">
            {{ $t('media.playAudio') }}
          </slot>
        </template>
      </Button>
    </slot>
  </template>

  <!-- Because teleport expects the target to be mounted before <Teleport> is, it needs to be loaded async -->
  <AsyncWrapper>
    <Teleport
      :disabled="isInline || !isActive"
      to="#audio-embed-outlet"
    >
      <div
        v-show="isInline || isActive"
        :class="{ 'grid-cols-[minmax(min-content,1fr)_minmax(0,56rem)_minmax(min-content,1fr)]': !isInline }"
        class="grid w-full items-center gap-8 whitespace-nowrap"
      >
        <Button
          v-if="!isInline"
          class="justify-self-start text-[var(--media-color,var(--theme-10,currentColor))] outline-[var(--media-color,var(--theme-10,currentColor))] hover:text-[var(--media-bg-color,var(--theme-50,currentColor))] hover:not-disabled:bg-[var(--media-color,var(--theme-10,currentColor))]"
          size="lg"
          variant="outline"
          @click="close()"
        >
          <font-awesome-icon :icon="faXmark" />
          {{ $t('media.closeAudio') }}
        </Button>

        <media-player
          ref="playerEl"
          :load
          :src="internalSrc"
          class="rounded-xl data-[focus]:ring"
          v-bind="attrsWithoutSrc"
          view-type="audio"
          out
          @pause="isPlaying = false"
          @play="isPlaying = isActive = true"
          @source-change="isPlaying = !(playerEl?.paused ?? true)"
        >
          <slot
            :activate
            :close
            :is-playing
            :pause="() => playerEl?.pause()"
            :play="() => playerEl?.play()"
          >
            <media-provider class="!hidden">
              <Captions
                v-if="mediaId"
                :media-id
                kind="metadata"
              />
            </media-provider>

            <slot
              :activate
              :close
              :is-playing
              :pause="() => playerEl?.pause()"
              :play="() => playerEl?.play()"
              name="controls"
            >
              <div class="m-auto flex w-full items-center gap-8 tabular-nums">
                <media-play-button
                  aria-hidden="true"
                  role="presentation"
                  tabindex="-1"
                >
                  <Button
                    :aria-label="isPlaying ? $t('media.pauseAudio') : $t('media.playAudio')"
                    :data-pendo
                    class="bg-(--media-color,var(--theme-10,currentColor)) text-(--media-bg-color,var(--theme-50,currentColor)) hover:opacity-90 hover:not-disabled:text-(--media-bg-color,var(--theme-50,currentColor))"
                    shape="square"
                    size="xl"
                  >
                    <font-awesome-icon
                      :class="{ 'translate-x-[5%]': !isPlaying }"
                      :icon="isPlaying ? faPause : faPlay"
                    />
                  </Button>
                </media-play-button>

                <media-time type="current" />
                <TimeSlider />
                <media-time type="duration" />

                <PopoverRoot>
                  <PopoverAnchor as-child>
                    <Tooltip
                      :content="t('media.changeSpeed')"
                      side="top"
                    >
                      <PopoverTrigger as-child>
                        <Button
                          :class="{ 'hover:not-disabled:text-(--media-bg-color,var(--theme-50,currentColor))': !isInline }"
                          :shade="isInline ? 'dark' : 'light'"
                          class="text-(--media-color) hover:not-disabled:bg-(--media-color,var(--theme-10,currentColor)) focus-visible:bg-(--media-bg-color,var(--theme-10,currentColor))"
                          size="lg"
                          variant="ghost"
                        >
                          {{ `${playerEl?.playbackRate}x` }}
                        </Button>
                      </PopoverTrigger>
                    </Tooltip>
                  </PopoverAnchor>

                  <PopoverContent
                    :side-offset="10"
                    class="animate-tooltip-in z-[101] rounded-xl bg-white text-lg shadow-xl"
                    side="top"
                  >
                    <media-speed-radio-group
                      :normal-label="t('media.normalSpeed')"
                      :rates="[0.6, 0.7, 0.8, 0.9, 1, 1.25]"
                      class="flex size-full flex-col p-2 text-(--theme-50)"
                    >
                      <template>
                        <media-radio class="group relative flex w-full cursor-pointer items-center rounded-lg p-2.5 hover:bg-(--theme-10) focus-visible:ring">
                          <span class="hidden items-center group-data-[checked]:flex">
                            <font-awesome-icon :icon="faCircleDot" />
                          </span>
                          <span class="flex items-center group-data-[checked]:hidden">
                            <font-awesome-icon :icon="faCircle" />
                          </span>
                          <span class="ml-2" data-part="label" />
                        </media-radio>
                      </template>
                    </media-speed-radio-group>
                  </PopoverContent>
                </PopoverRoot>

                <AuPopover v-if="aiGenerated" align="end">
                  <template #trigger>
                    <Button
                      v-if="isInline"
                      :data-pendo="PendoFeature.TextToSpeech.InfoButton"
                      class="-ml-6 text-(--media-color) hover:not-disabled:bg-(--media-color,var(--theme-10,currentColor))"
                      size="lg"
                      variant="ghost"
                    >
                      <font-awesome-icon :icon="faInfoCircle" />
                    </Button>
                    <button
                      v-else
                      class="underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:ring"
                      :data-pendo="PendoFeature.TextToSpeech.InfoButton"
                      v-text="t('tts.voice')"
                    />
                  </template>
                  <template #content>
                    <p class="p-2 text-(--theme-50)" v-text="t('tts.warning')" />
                  </template>
                </AuPopover>

                <IconInputSwitch
                  v-if="!isInline && cues.length > 0"
                  v-model="showTranscription"
                  :label="$t('labels.audio_transcription')"
                >
                  <font-awesome-icon :icon="faText" />
                </IconInputSwitch>
              </div>
              <!-- <media-audio-layout /> -->
            </slot>
          </slot>
        </media-player>
      </div>
    </Teleport>
  </AsyncWrapper>
</template>
